import React, { useState, useEffect } from 'react'
import './SignupModal.css'
import { registerUser, getSubscriptionPlans, createPaymentOrder, verifyPayment, sendSMSOTP, verifySMSOTP, resendSMSOTP, getVersionDetailsData, checkSignupStatus, registerUserMobile } from '../services/hc_apis'
import ReactCountryFlag from 'react-country-flag'
import { getCountries } from 'country-list'


const FEATURES_LIST = [
  "Be inspired by daily quotes",
  "Pick your habits from the Habit Gallery",
  "Mark 3 habits as your Hero Habits",
  "Reflect on your habit consistency",
  "Share daily quote and habit progress with your friends"
]

// localStorage utility functions
const STORAGE_KEYS = {
  SIGNUP_DATA: 'habitcircles_signup_data',
  CURRENT_STEP: 'habitcircles_current_step',
  USER_DATA: 'habitcircles_user_data'
}

const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data))
  } catch (error) {
    console.error('Error saving to localStorage:', error)
  }
}

const getFromLocalStorage = (key) => {
  try {
    const data = localStorage.getItem(key)
    return data ? JSON.parse(data) : null
  } catch (error) {
    console.error('Error reading from localStorage:', error)
    return null
  }
}

const clearLocalStorage = () => {
  try {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key)
    })
  } catch (error) {
    console.error('Error clearing localStorage:', error)
  }
}

const SignupModal = ({ onClose, group, version, renderAsPage = false, service = null }) => {
  // Calculate total price from platform and coach fees
  const calculateServiceTotal = () => {
    if (!service) return 0
    const platformFee = service.fee_platform_inr || 0
    const coachFee = service.fee_coach_inr || 0
    return platformFee + coachFee
  }

  const [currentStep, setCurrentStep] = useState(1) // 1: Registration, 2: SMS OTP, 3: Payment
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone_no: '',
    group_code: group?.group_code || ''
  })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState({ code: 'IN', dialCode: '+91', name: 'India' })
  const [showCountryPicker, setShowCountryPicker] = useState(false)
  const [countrySearchTerm, setCountrySearchTerm] = useState('')
  const [registeredUser, setRegisteredUser] = useState(null)
  const [paymentData, setPaymentData] = useState(null)
  const [paymentStatus, setPaymentStatus] = useState('pending')
  
  // SMS OTP states
  const [smsOTP, setSmsOTP] = useState('')
  const [smsOTPSent, setSmsOTPSent] = useState(false)
  const [smsOTPVerified, setSmsOTPVerified] = useState(false)
  const [smsOTPLoading, setSmsOTPLoading] = useState(false)
  const [smsOTPError, setSmsOTPError] = useState('')

  // Email OTP states - REMOVED (no longer needed)

  // Version state for mobile app download
  // const [version, setVersion] = useState(null)

  // Auto-clear error when user starts typing (but only after a delay)
  useEffect(() => {
    if (smsOTPError && smsOTP.length > 0) {
      // Clear error after 2 seconds to give user time to see it
      const timer = setTimeout(() => {
        console.log('Auto-clearing SMS OTP error after delay');
        setSmsOTPError('');
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [smsOTP, smsOTPError]);

  // Email OTP error clearing - REMOVED (no longer needed)

  // Clear localStorage on component mount to start fresh
  // Step determination will be done by API response, not localStorage
  useEffect(() => {
    clearLocalStorage();
  }, []);

  // // Fetch version details for mobile app download
  // useEffect(() => {
  //   const getVersionDetails = async () => {
  //     try {
  //       console.log('Fetching version details...');
  //       const result = await getVersionDetailsData();
  //       console.log('Version API response:', result);
  //       if (result.status === 200) {
  //         console.log('Version data:', result?.data?.data);
  //         setVersion(result?.data?.data);
  //       } else {
  //         console.error('Version API failed:', result);
  //         // Retry after 2 seconds
  //         setTimeout(() => {
  //           console.log('Retrying version fetch...');
  //           getVersionDetails();
  //         }, 2000);
  //       }
  //     } catch (error) {
  //       console.error('Failed to fetch version details:', error);
  //       // Retry after 3 seconds
  //       setTimeout(() => {
  //         console.log('Retrying version fetch after error...');
  //         getVersionDetails();
  //       }, 3000);
  //     }
  //   };
    
  //   getVersionDetails();
  // }, []);

  // Update form data when group prop changes
  useEffect(() => {
    if (group?.group_code) {
      setFormData(prev => ({
        ...prev,
        group_code: group.group_code
      }))
    }
  }, [group])

  // Initialize modal to start fresh signup
  useEffect(() => {
    setCurrentStep(1)
  }, [])

  // Close country picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCountryPicker && !event.target.closest('.country-code-container')) {
        setShowCountryPicker(false)
        setCountrySearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showCountryPicker])

  const validateForm = () => {
    const newErrors = {}

    if (!formData.email) {
      newErrors.email = 'Please enter Email Id!'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!formData.name) {
      newErrors.name = 'Please enter Name!'
    }

    if (!formData.phone_no) {
      newErrors.phone_no = 'Please enter Mobile Number!'
    } else if (!/^[0-9]{10}$/.test(formData.phone_no)) {
      newErrors.phone_no = 'Mobile Number must be exactly 10 digits'
    }

    if (!formData.group_code) {
      newErrors.group_code = 'Please enter Group code!'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value
    }
    
    setFormData(newFormData)

    // Save form data to localStorage (only if we're in step 1)
    if (currentStep === 1) {
      saveToLocalStorage(STORAGE_KEYS.SIGNUP_DATA, newFormData)
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const handleRegistration = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      // First, fetch payment plans to ensure they're available
      await fetchPaymentPlans()

      // Then register the user using mobile endpoint
      const response = await registerUserMobile({
        ...formData,
        country_code: selectedCountry.dialCode,
      })
      const responseData = response.data?.data

      // Handle different response statuses
      // If user is already registered but trying to buy an offering, allow them to proceed
      if (responseData.status === 'already_registered') {
        if (service) {
          // User is buying an offering - allow them to proceed to payment
          const userData = {
            id: responseData.user_id,
            email: responseData.email || formData.email,
            name: responseData.name || formData.name,
            phone_no: responseData.phone_no || formData.phone_no,
            country_code: responseData.country_code || selectedCountry.dialCode,
            activation_code: responseData.activation_code,
            phone_verified: responseData.phone_verified || true,
            email_verified: responseData.email_verified || true
          }
          
          setRegisteredUser(userData)
          
          // Save progress to localStorage
          saveToLocalStorage(STORAGE_KEYS.SIGNUP_DATA, formData)
          saveToLocalStorage(STORAGE_KEYS.USER_DATA, userData)
          
          // Skip directly to payment step for offering purchase
          setCurrentStep(3)
          saveToLocalStorage(STORAGE_KEYS.CURRENT_STEP, 3)
          return
        } else {
          // Regular subscription - user already has one, show message and close
          alert('You are already registered and active!')
          onClose()
          return
        }
      }

      // Handle existing users - redirect to appropriate step based on DB status
      if (responseData.status === 'existing_user' || responseData.status === 'need_phone_verification' || 
          responseData.status === 'need_email_verification' || responseData.status === 'ready_for_payment') {
        
        
        // Set user data from backend response
        const userData = {
          id: responseData.user_id,
          email: responseData.email || formData.email,
          name: responseData.name || formData.name,
          phone_no: responseData.phone_no || formData.phone_no,
          country_code: responseData.country_code || selectedCountry.dialCode,
          activation_code: responseData.activation_code,
          phone_verified: responseData.phone_verified || false,
          email_verified: responseData.email_verified || false
        }
        
        
        setRegisteredUser(userData)
        
        // Determine step based on verification status from database
        let targetStep = 1;
        let message = '';
        
        if (userData.phone_verified) {
          // Phone verified - go to payment (skip email verification)
          targetStep = 3; // Changed from 4 to 3 since we removed email verification
          message = 'Phone verified! Please proceed to payment.';
        } else if (!userData.phone_verified) {
          // Need phone verification first
          targetStep = 2;
          message = 'We found your account. Please verify your phone number to continue.';
        }
        
        // alert(message);
        setCurrentStep(targetStep);
        
        // Save minimal data to localStorage for session continuity
        saveToLocalStorage(STORAGE_KEYS.SIGNUP_DATA, formData);
        saveToLocalStorage(STORAGE_KEYS.USER_DATA, userData);
        saveToLocalStorage(STORAGE_KEYS.CURRENT_STEP, targetStep);
        
        // Send SMS OTP if needed
        if (targetStep === 2) {
          await sendSMSOTPForVerification();
        }
        
        return; // Exit early for existing users
      }

      // Handle new user registration
      console.log('Backend response for new user:', responseData);
      
      const userData = {
        id: responseData.user_id,
        email: responseData.email || formData.email,
        name: responseData.name || formData.name,
        phone_no: responseData.phone_no || formData.phone_no,
        country_code: responseData.country_code || selectedCountry.dialCode,
        activation_code: responseData.activation_code,
        phone_verified: false,
        email_verified: true // Set to true since we skip email verification
      }
      

      setRegisteredUser(userData)

      // Save progress to localStorage
      saveToLocalStorage(STORAGE_KEYS.SIGNUP_DATA, formData)
      saveToLocalStorage(STORAGE_KEYS.USER_DATA, userData)

      // New users always start with SMS OTP verification
      setCurrentStep(2)
      saveToLocalStorage(STORAGE_KEYS.CURRENT_STEP, 2)
      
      // Send SMS OTP for new users
      await sendSMSOTPForVerification()

    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Registration failed!'
      
      // Check for specific validation errors
      if (errorMessage.toLowerCase().includes('email') && errorMessage.toLowerCase().includes('already exists')) {
        setErrors({ email: 'This email address is already registered. Please use a different email.' })
      } else if (errorMessage.toLowerCase().includes('phone') && errorMessage.toLowerCase().includes('already exists')) {
        setErrors({ phone_no: 'This phone number is already registered. Please use a different phone number.' })
      } else {
        alert(errorMessage)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPaymentPlans = async () => {
    try {
      const response = await getSubscriptionPlans()
      const plans = response.data.data.plans
      const yearlyPlan = plans.find(p => p.plan_type === 'yearly')
      setPaymentData(yearlyPlan)
    } catch (error) {
      console.error('Error fetching payment plans:', error)
      alert('Failed to load payment options')
    }
  }






  // SMS OTP Functions
  const sendSMSOTPForVerification = async () => {
    try {
      setSmsOTPLoading(true)
      setSmsOTPError('')
      
      // Get user_id from registeredUser or localStorage
      const userData = registeredUser || getFromLocalStorage(STORAGE_KEYS.USER_DATA)
      if (!userData?.id) {
        throw new Error('User ID not found. Please register first.')
      }
      
      await sendSMSOTP({
        phone_number: formData.phone_no,
        country_code: selectedCountry.dialCode,
        user_id: userData.id
      })
      
      setSmsOTPSent(true)
    } catch (error) {
      console.error('Error sending SMS OTP:', error)
      setSmsOTPError('Failed to send SMS OTP. Please try again.')
    } finally {
      setSmsOTPLoading(false)
    }
  }

  const verifySMSOTPCode = async () => {
    console.log('Frontend OTP Verification Debug:', {
      smsOTP,
      smsOTPVerified,
      smsOTPLoading,
      smsOTPError
    });

    if (!smsOTP) {
      setSmsOTPError('Please enter the OTP')
      return false
    }

    try {
      setSmsOTPLoading(true)
      setSmsOTPError('')
      
      // Get user_id from registeredUser or localStorage
      const userData = registeredUser || getFromLocalStorage(STORAGE_KEYS.USER_DATA)
      if (!userData?.id) {
        throw new Error('User ID not found. Please register first.')
      }
      
      console.log('Sending OTP verification request:', {
        phone_number: formData.phone_no,
        country_code: selectedCountry.dialCode,
        otp: smsOTP,
        user_id: userData.id
      });
      
      const response = await verifySMSOTP({
        phone_number: formData.phone_no,
        country_code: selectedCountry.dialCode,
        otp: smsOTP,
        user_id: userData.id
      })

      console.log('OTP verification response:', response.data);

      if (response.data.status === 200) {
        setSmsOTPVerified(true)
        setSmsOTPError('')
        return true
      } else {
        setSmsOTPError('Invalid OTP. Please try again.')
        return false
      }
    } catch (error) {
      console.error('Error verifying SMS OTP:', error)
      const errorMessage = error.response?.data?.message || 'OTP verification failed'
      setSmsOTPError(errorMessage)
      
      // If too many attempts, clear the OTP field but keep resend button available
      if (errorMessage.includes('Too many failed attempts')) {
        setSmsOTP('')
        // Don't set smsOTPSent to false - keep the resend button available
        alert('Too many failed attempts. Please request a new OTP.')
      } else {
        // For other errors, keep the OTP field but ensure it's editable
        // Force a small delay to ensure state updates are processed
        setTimeout(() => {
          console.log('Error state set, input should be editable now');
        }, 100);
      }
      
      return false
    } finally {
      setSmsOTPLoading(false)
    }
  }

  const resendSMSOTPCode = async () => {
    try {
      setSmsOTPLoading(true)
      setSmsOTPError('')
      
      // Get user_id from registeredUser or localStorage
      const userData = registeredUser || getFromLocalStorage(STORAGE_KEYS.USER_DATA)
      if (!userData?.id) {
        throw new Error('User ID not found. Please register first.')
      }
      
      await resendSMSOTP({
        phone_number: formData.phone_no,
        country_code: selectedCountry.dialCode,
        user_id: userData.id
      })
      
      setSmsOTP('')
      alert('SMS OTP resent successfully')
    } catch (error) {
      console.error('Error resending SMS OTP:', error)
      setSmsOTPError('Failed to resend SMS OTP')
    } finally {
      setSmsOTPLoading(false)
    }
  }

  // Email OTP Functions
  // Email OTP functions - REMOVED (no longer needed)

  // verifyEmailOTPCode function - REMOVED (no longer needed)

  // resendEmailOTPCode function - REMOVED (no longer needed)

  const handleSMSOTPVerification = async () => {
    const verified = await verifySMSOTPCode()
    if (verified) {
      // Save progress to localStorage
      saveToLocalStorage(STORAGE_KEYS.CURRENT_STEP, 3)
      
      // Proceed directly to payment step (skip email verification)
      setCurrentStep(3)
    }
  }

  // Email OTP verification handler - REMOVED (no longer needed)

  const createPaymentOrderAPI = async () => {
    try {
      setIsLoading(true)
      const orderData = {
        user_id: registeredUser.id,
        plan_type: 'yearly'
      }
      
      // Add service_id if service is provided
      if (service && service._id) {
        orderData.service_id = service._id
      }
      
      // Add group_id - prefer from service, then from group prop
      if (service && service.user_group_id && service.user_group_id._id) {
        orderData.group_id = service.user_group_id._id
      } else if (group && group._id) {
        orderData.group_id = group._id
      }
      
      const response = await createPaymentOrder(orderData)

      if (response.data.status === 200 && response.data.data) {
        return response.data.data
      } else {
        console.error('Payment order failed:', response.data)
        throw new Error(response.data.message || 'Failed to create payment order')
      }
    } catch (error) {
      console.error('Error creating payment order:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script')
      script.src = 'https://checkout.razorpay.com/v1/checkout.js'
      script.onload = () => resolve(true)
      script.onerror = () => resolve(false)
      document.body.appendChild(script)
    })
  }

  const handlePayment = async () => {
    try {
      setIsLoading(true)

      // Load Razorpay script
      const isLoaded = await loadRazorpayScript()
      if (!isLoaded) {
        alert('Failed to load payment gateway')
        return
      }

      // Create payment order
      const orderData = await createPaymentOrderAPI()

      // Use amount from API response (backend calculates it correctly for offerings)
      // Fallback to calculated service total or paymentData amount
      const paymentAmount = orderData.amount || (service ? calculateServiceTotal() : (paymentData?.amount || 0))
      const paymentCurrency = orderData.currency || paymentData?.currency || 'INR'

      // Truncate description to 255 characters (Razorpay limit)
      const fullDescription = orderData.plan_details?.description || paymentData?.description || (service ? (service.title || service.name) : 'Payment');
      // Ensure it's a string and truncate to 255 characters
      const truncatedDescription = String(fullDescription || 'Payment').substring(0, 255);
      
      // Debug log to check description length
      if (fullDescription && fullDescription.length > 255) {
        console.warn('Description was truncated from', fullDescription.length, 'to 255 characters');
      }

      // Final validation - ensure description is exactly 255 or less
      const finalDescription = truncatedDescription.length > 255 
        ? truncatedDescription.substring(0, 255) 
        : truncatedDescription;

      // Log final description for debugging
      console.log('Razorpay description length:', finalDescription.length, 'Description:', finalDescription);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: paymentAmount * 100, // Convert to paise
        currency: paymentCurrency,
        name: 'HabitCircles',
        description: finalDescription,
        order_id: orderData.order_id,
        handler: async function (response) {
          try {
            // Verify payment on backend
            const verifyData = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              user_id: registeredUser.id
            }
            
            // Add service_id if service is provided
            if (service && service._id) {
              verifyData.service_id = service._id
            }
            
            // Add group_id - prefer from service, then from group prop
            if (service && service.user_group_id && service.user_group_id._id) {
              verifyData.group_id = service.user_group_id._id
            } else if (group && group._id) {
              verifyData.group_id = group._id
            }
            
            const verifyResponse = await verifyPayment(verifyData)

            if (verifyResponse.data.status === 200) {
              // Don't clear localStorage immediately - keep user data for success modal
              setPaymentStatus('success')
              // Remove auto-close - let user close manually
            } else {
              setPaymentStatus('failed')
              alert('Payment verification failed. Please try again.')
            }
          } catch (err) {
            alert('Payment verification failed')
            console.error('Payment verification error:', err)
          }
        },
        prefill: {
          name: registeredUser.name,
          email: registeredUser.email,
          contact: `${selectedCountry.dialCode}${formData.phone_no}`
        },
        notes: {
          user_id: registeredUser.id,
          plan_type: 'yearly'
        },
        theme: {
          color: '#4F46E5'
        },
        modal: {
          ondismiss: function () {
            setPaymentStatus('cancelled')
          }
        }
      }

      const razorpay = new window.Razorpay(options)
      razorpay.open()
    } catch (error) {
      setPaymentStatus('failed')
      alert('Failed to initiate payment. Please try again.')
      console.error('Payment initiation error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Country codes with dial codes - focusing on most common ones
  const countryOptions = [
    { code: 'IN', dialCode: '+91', name: 'India' },
    { code: 'US', dialCode: '+1', name: 'United States' },
    { code: 'GB', dialCode: '+44', name: 'United Kingdom' },
    { code: 'AU', dialCode: '+61', name: 'Australia' },
    { code: 'CA', dialCode: '+1', name: 'Canada' },
    { code: 'DE', dialCode: '+49', name: 'Germany' },
    { code: 'FR', dialCode: '+33', name: 'France' },
    { code: 'IT', dialCode: '+39', name: 'Italy' },
    { code: 'ES', dialCode: '+34', name: 'Spain' },
    { code: 'NL', dialCode: '+31', name: 'Netherlands' },
    { code: 'SE', dialCode: '+46', name: 'Sweden' },
    { code: 'NO', dialCode: '+47', name: 'Norway' },
    { code: 'DK', dialCode: '+45', name: 'Denmark' },
    { code: 'FI', dialCode: '+358', name: 'Finland' },
    { code: 'CH', dialCode: '+41', name: 'Switzerland' },
    { code: 'AT', dialCode: '+43', name: 'Austria' },
    { code: 'BE', dialCode: '+32', name: 'Belgium' },
    { code: 'IE', dialCode: '+353', name: 'Ireland' },
    { code: 'PT', dialCode: '+351', name: 'Portugal' },
    { code: 'GR', dialCode: '+30', name: 'Greece' },
    { code: 'PL', dialCode: '+48', name: 'Poland' },
    { code: 'CZ', dialCode: '+420', name: 'Czech Republic' },
    { code: 'HU', dialCode: '+36', name: 'Hungary' },
    { code: 'RO', dialCode: '+40', name: 'Romania' },
    { code: 'BG', dialCode: '+359', name: 'Bulgaria' },
    { code: 'HR', dialCode: '+385', name: 'Croatia' },
    { code: 'SI', dialCode: '+386', name: 'Slovenia' },
    { code: 'SK', dialCode: '+421', name: 'Slovakia' },
    { code: 'LT', dialCode: '+370', name: 'Lithuania' },
    { code: 'LV', dialCode: '+371', name: 'Latvia' },
    { code: 'EE', dialCode: '+372', name: 'Estonia' },
    { code: 'CN', dialCode: '+86', name: 'China' },
    { code: 'JP', dialCode: '+81', name: 'Japan' },
    { code: 'KR', dialCode: '+82', name: 'South Korea' },
    { code: 'SG', dialCode: '+65', name: 'Singapore' },
    { code: 'HK', dialCode: '+852', name: 'Hong Kong' },
    { code: 'TW', dialCode: '+886', name: 'Taiwan' },
    { code: 'TH', dialCode: '+66', name: 'Thailand' },
    { code: 'MY', dialCode: '+60', name: 'Malaysia' },
    { code: 'ID', dialCode: '+62', name: 'Indonesia' },
    { code: 'PH', dialCode: '+63', name: 'Philippines' },
    { code: 'VN', dialCode: '+84', name: 'Vietnam' },
    { code: 'BD', dialCode: '+880', name: 'Bangladesh' },
    { code: 'PK', dialCode: '+92', name: 'Pakistan' },
    { code: 'LK', dialCode: '+94', name: 'Sri Lanka' },
    { code: 'NP', dialCode: '+977', name: 'Nepal' },
    { code: 'BT', dialCode: '+975', name: 'Bhutan' },
    { code: 'MV', dialCode: '+960', name: 'Maldives' },
    { code: 'AF', dialCode: '+93', name: 'Afghanistan' },
    { code: 'IR', dialCode: '+98', name: 'Iran' },
    { code: 'IQ', dialCode: '+964', name: 'Iraq' },
    { code: 'SA', dialCode: '+966', name: 'Saudi Arabia' },
    { code: 'AE', dialCode: '+971', name: 'United Arab Emirates' },
    { code: 'QA', dialCode: '+974', name: 'Qatar' },
    { code: 'KW', dialCode: '+965', name: 'Kuwait' },
    { code: 'BH', dialCode: '+973', name: 'Bahrain' },
    { code: 'OM', dialCode: '+968', name: 'Oman' },
    { code: 'JO', dialCode: '+962', name: 'Jordan' },
    { code: 'LB', dialCode: '+961', name: 'Lebanon' },
    { code: 'SY', dialCode: '+963', name: 'Syria' },
    { code: 'IL', dialCode: '+972', name: 'Israel' },
    { code: 'TR', dialCode: '+90', name: 'Turkey' },
    { code: 'EG', dialCode: '+20', name: 'Egypt' },
    { code: 'LY', dialCode: '+218', name: 'Libya' },
    { code: 'TN', dialCode: '+216', name: 'Tunisia' },
    { code: 'DZ', dialCode: '+213', name: 'Algeria' },
    { code: 'MA', dialCode: '+212', name: 'Morocco' },
    { code: 'ZA', dialCode: '+27', name: 'South Africa' },
    { code: 'NG', dialCode: '+234', name: 'Nigeria' },
    { code: 'KE', dialCode: '+254', name: 'Kenya' },
    { code: 'GH', dialCode: '+233', name: 'Ghana' },
    { code: 'ET', dialCode: '+251', name: 'Ethiopia' },
    { code: 'UG', dialCode: '+256', name: 'Uganda' },
    { code: 'TZ', dialCode: '+255', name: 'Tanzania' },
    { code: 'ZW', dialCode: '+263', name: 'Zimbabwe' },
    { code: 'ZM', dialCode: '+260', name: 'Zambia' },
    { code: 'BW', dialCode: '+267', name: 'Botswana' },
    { code: 'NA', dialCode: '+264', name: 'Namibia' },
    { code: 'MW', dialCode: '+265', name: 'Malawi' },
    { code: 'MZ', dialCode: '+258', name: 'Mozambique' },
    { code: 'MG', dialCode: '+261', name: 'Madagascar' },
    { code: 'MU', dialCode: '+230', name: 'Mauritius' },
    { code: 'SC', dialCode: '+248', name: 'Seychelles' },
    { code: 'BR', dialCode: '+55', name: 'Brazil' },
    { code: 'AR', dialCode: '+54', name: 'Argentina' },
    { code: 'CL', dialCode: '+56', name: 'Chile' },
    { code: 'CO', dialCode: '+57', name: 'Colombia' },
    { code: 'PE', dialCode: '+51', name: 'Peru' },
    { code: 'VE', dialCode: '+58', name: 'Venezuela' },
    { code: 'EC', dialCode: '+593', name: 'Ecuador' },
    { code: 'BO', dialCode: '+591', name: 'Bolivia' },
    { code: 'PY', dialCode: '+595', name: 'Paraguay' },
    { code: 'UY', dialCode: '+598', name: 'Uruguay' },
    { code: 'GY', dialCode: '+592', name: 'Guyana' },
    { code: 'SR', dialCode: '+597', name: 'Suriname' },
    { code: 'MX', dialCode: '+52', name: 'Mexico' },
    { code: 'GT', dialCode: '+502', name: 'Guatemala' },
    { code: 'BZ', dialCode: '+501', name: 'Belize' },
    { code: 'SV', dialCode: '+503', name: 'El Salvador' },
    { code: 'HN', dialCode: '+504', name: 'Honduras' },
    { code: 'NI', dialCode: '+505', name: 'Nicaragua' },
    { code: 'CR', dialCode: '+506', name: 'Costa Rica' },
    { code: 'PA', dialCode: '+507', name: 'Panama' },
    { code: 'CU', dialCode: '+53', name: 'Cuba' },
    { code: 'JM', dialCode: '+1876', name: 'Jamaica' },
    { code: 'HT', dialCode: '+509', name: 'Haiti' },
    { code: 'DO', dialCode: '+1809', name: 'Dominican Republic' },
    { code: 'PR', dialCode: '+1787', name: 'Puerto Rico' },
    { code: 'TT', dialCode: '+1868', name: 'Trinidad and Tobago' },
    { code: 'BB', dialCode: '+1246', name: 'Barbados' },
    { code: 'AG', dialCode: '+1268', name: 'Antigua and Barbuda' },
    { code: 'DM', dialCode: '+1767', name: 'Dominica' },
    { code: 'GD', dialCode: '+1473', name: 'Grenada' },
    { code: 'KN', dialCode: '+1869', name: 'Saint Kitts and Nevis' },
    { code: 'LC', dialCode: '+1758', name: 'Saint Lucia' },
    { code: 'VC', dialCode: '+1784', name: 'Saint Vincent and the Grenadines' },
    { code: 'NZ', dialCode: '+64', name: 'New Zealand' },
    { code: 'FJ', dialCode: '+679', name: 'Fiji' },
    { code: 'PG', dialCode: '+675', name: 'Papua New Guinea' },
    { code: 'SB', dialCode: '+677', name: 'Solomon Islands' },
    { code: 'VU', dialCode: '+678', name: 'Vanuatu' },
    { code: 'NC', dialCode: '+687', name: 'New Caledonia' },
    { code: 'PF', dialCode: '+689', name: 'French Polynesia' },
    { code: 'WS', dialCode: '+685', name: 'Samoa' },
    { code: 'TO', dialCode: '+676', name: 'Tonga' },
    { code: 'KI', dialCode: '+686', name: 'Kiribati' },
    { code: 'TV', dialCode: '+688', name: 'Tuvalu' },
    { code: 'NR', dialCode: '+674', name: 'Nauru' },
    { code: 'PW', dialCode: '+680', name: 'Palau' },
    { code: 'FM', dialCode: '+691', name: 'Micronesia' },
    { code: 'MH', dialCode: '+692', name: 'Marshall Islands' },
    { code: 'RU', dialCode: '+7', name: 'Russia' },
    { code: 'KZ', dialCode: '+7', name: 'Kazakhstan' },
    { code: 'UZ', dialCode: '+998', name: 'Uzbekistan' },
    { code: 'TM', dialCode: '+993', name: 'Turkmenistan' },
    { code: 'TJ', dialCode: '+992', name: 'Tajikistan' },
    { code: 'KG', dialCode: '+996', name: 'Kyrgyzstan' },
    { code: 'MN', dialCode: '+976', name: 'Mongolia' },
    { code: 'GE', dialCode: '+995', name: 'Georgia' },
    { code: 'AM', dialCode: '+374', name: 'Armenia' },
    { code: 'AZ', dialCode: '+994', name: 'Azerbaijan' },
    { code: 'BY', dialCode: '+375', name: 'Belarus' },
    { code: 'MD', dialCode: '+373', name: 'Moldova' },
    { code: 'UA', dialCode: '+380', name: 'Ukraine' },
  ]

  // Filter countries based on search term
  const filteredCountries = countryOptions.filter(country =>
    country.name.toLowerCase().includes(countrySearchTerm.toLowerCase()) ||
    country.dialCode.includes(countrySearchTerm) ||
    country.code.toLowerCase().includes(countrySearchTerm.toLowerCase())
  )

  const renderRegistrationStep = () => (
    <form onSubmit={handleRegistration} className="signup-form">
      {/* <div className="form-group phone-group">
        <label>Mobile Number</label>
        <div className="phone-input-container">
          <div className="country-code-container">
            <button
              type="button"
              className="country-code-btn"
              onClick={() => {
                setShowCountryPicker(!showCountryPicker);
                if (showCountryPicker) {
                  setCountrySearchTerm('');
                }
              }}
            >
              <ReactCountryFlag
                countryCode={selectedCountry.code}
                svg
                style={{
                  width: '20px',
                  height: '15px',
                  marginRight: '8px'
                }}
                title={selectedCountry.name}
              />
              {selectedCountry.dialCode}
            </button>
            {showCountryPicker && (
              <div className="country-picker" onClick={(e) => e.stopPropagation()}>
                <div className="country-search-container">
                  <input
                    type="text"
                    placeholder="Search country..."
                    value={countrySearchTerm}
                    onChange={(e) => setCountrySearchTerm(e.target.value)}
                    className="country-search-input"
                    autoFocus
                  />
                </div>
                <div className="country-list">
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map((country) => (
                      <button
                        key={country.code}
                        type="button"
                        className="country-option"
                        onClick={() => {
                          setSelectedCountry(country);
                          setShowCountryPicker(false);
                          setCountrySearchTerm('');
                        }}
                      >
                        <ReactCountryFlag
                          countryCode={country.code}
                          svg
                          style={{
                            width: '20px',
                            height: '15px',
                            marginRight: '8px'
                          }}
                          title={country.name}
                        />
                        <span>{country.name}</span>
                        <span>{country.dialCode}</span>
                      </button>
                    ))
                  ) : (
                    <div className="no-results">
                      No countries found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <input
            type="tel"
            placeholder="Mobile Number"
            value={formData.phone_no}
            onChange={(e) => handleInputChange('phone_no', e.target.value)}
            className={`phone-input ${errors.phone_no ? 'error' : ''}`}
          />
        </div>
        {errors.phone_no && <span className="error-message">{errors.phone_no}</span>}
      </div> */}

      <div className="form-group">
        <label>Email ID</label>
        <input
          type="email"
          placeholder="Email ID"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          className={errors.email ? 'error' : ''}
        />
        {errors.email && <span className="error-message">{errors.email}</span>}
      </div>

      <div className="form-group">
        <label>Name</label>
        <input
          type="text"
          placeholder="Name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          className={errors.name ? 'error' : ''}
        />
        {errors.name && <span className="error-message">{errors.name}</span>}
      </div>

      <div className="form-group phone-group">
        <label>Mobile Number</label>
        <div className="phone-input-container">
          <div className="country-code-container">
            <button
              type="button"
              className="country-code-btn"
              onClick={() => {
                setShowCountryPicker(!showCountryPicker)
                if (showCountryPicker) {
                  setCountrySearchTerm('')
                }
              }}
            >
              <ReactCountryFlag
                countryCode={selectedCountry.code}
                svg
                style={{
                  width: '20px',
                  height: '15px',
                  marginRight: '8px'
                }}
                title={selectedCountry.name}
              />
              {selectedCountry.dialCode}
            </button>
            {showCountryPicker && (
              <div className="country-picker" onClick={(e) => e.stopPropagation()}>
                <div className="country-search-container">
                  <input
                    type="text"
                    placeholder="Search country..."
                    value={countrySearchTerm}
                    onChange={(e) => setCountrySearchTerm(e.target.value)}
                    className="country-search-input"
                    autoFocus
                  />
                </div>
                <div className="country-list">
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map((country) => (
                      <button
                        key={country.code}
                        type="button"
                        className="country-option"
                        onClick={() => {
                          setSelectedCountry(country)
                          setShowCountryPicker(false)
                          setCountrySearchTerm('')
                        }}
                      >
                        <ReactCountryFlag
                          countryCode={country.code}
                          svg
                          style={{
                            width: '20px',
                            height: '15px',
                            marginRight: '8px'
                          }}
                          title={country.name}
                        />
                        <span>{country.name}</span>
                        <span>{country.dialCode}</span>
                      </button>
                    ))
                  ) : (
                    <div className="no-results">
                      No countries found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <input
            type="tel"
            placeholder="Mobile Number"
            value={formData.phone_no}
            onChange={(e) => handleInputChange('phone_no', e.target.value)}
            className={`phone-input ${errors.phone_no ? 'error' : ''}`}
          />
        </div>
        {errors.phone_no && <span className="error-message">{errors.phone_no}</span>}
      </div>

      <div className="form-group">
        <label>Group Code</label>
        <input
          type="text"
          placeholder="Group Code"
          value={formData.group_code}
          onChange={(e) => handleInputChange('group_code', e.target.value)}
          className={errors.group_code ? 'error' : ''}
        />
        {errors.group_code && <span className="error-message">{errors.group_code}</span>}
      </div>

      {/* Service Summary Section */}
      {service && (
        <div className="service-summary-card">
          <div className="service-summary-header">
            <span className="service-summary-label">Service</span>
            <span className="service-summary-price">₹{calculateServiceTotal().toFixed(2)}</span>
          </div>
          <div className="service-summary-name" style={{ textAlign: 'left' }}>
            {service.title || service.name || 'Service'}
          </div>
          <div className="service-summary-divider"></div>
          <div className="service-summary-total">
            <span className="service-summary-total-label">Amount to be paid :</span>
            <span className="service-summary-total-amount">₹{calculateServiceTotal().toFixed(2)}</span>
          </div>
        </div>
      )}

      <p style={{ fontSize: '16px', color: 'red', textAlign: 'left' }}>Android app available, iOS app coming soon!</p>
      <button
        type="submit"
        className="submit-btn"
        disabled={isLoading}
      >
        {isLoading ? 'Processing...' : service ? `Proceed to pay ₹${calculateServiceTotal().toFixed(2)}` : 'Proceed to pay'}
      </button>
    </form>
  )



  const renderSMSOTPVerificationStep = () => {
    console.log('Rendering SMS OTP Step:', {
      smsOTP,
      smsOTPVerified,
      smsOTPLoading,
      smsOTPError,
      smsOTPSent,
      shouldShowResendButton: (smsOTPSent && !smsOTPVerified) || smsOTPError
    });
    
    return (
    <div className="sms-otp-verification-step">
      <div className="otp-header">
        <h3>Verify Your Mobile Number</h3>
        <p>We've sent a verification code to {selectedCountry.dialCode}{formData.phone_no}</p>
      </div>

      <div className="otp-form">
        <div className="form-group">
          <label>Enter 6-digit SMS code</label>
          <div className="otp-input-container">
            <input
              type="text"
              placeholder="Enter 6-digit code"
              value={smsOTP}
              onChange={(e) => {
                console.log('OTP Input Change:', {
                  newValue: e.target.value,
                  currentValue: smsOTP,
                  hasError: !!smsOTPError,
                  smsOTPVerified,
                  smsOTPLoading
                });
                setSmsOTP(e.target.value)
                // Clear error when user starts typing (but not immediately)
                if (smsOTPError) {
                  // Use a small delay to allow user to see the error first
                  setTimeout(() => {
                    setSmsOTPError('')
                  }, 1000);
                }
              }}
              onFocus={(e) => {
                console.log('OTP Input Focused:', {
                  value: e.target.value,
                  smsOTPVerified,
                  smsOTPLoading,
                  smsOTPError
                });
                // Don't clear error on focus - let user see the error message
              }}
              onBlur={(e) => {
                console.log('OTP Input Blurred:', {
                  value: e.target.value,
                  smsOTPVerified,
                  smsOTPLoading,
                  smsOTPError
                });
              }}
              maxLength="6"
              className={`otp-input ${smsOTPError ? 'error' : ''} ${smsOTPVerified ? 'verified' : ''}`}
              disabled={smsOTPVerified}
            />
            <button
              type="button"
              className="verify-btn"
              onClick={handleSMSOTPVerification}
              disabled={smsOTPVerified || smsOTPLoading}
            >
              {smsOTPVerified ? 'Verified' : 'Verify'}
            </button>
          </div>
        {/* {smsOTPError && (
          <div>
            <span className="error-message">{smsOTPError}</span>
            <button
              type="button"
              onClick={() => {
                console.log('Clearing OTP field manually');
                setSmsOTP('');
                setSmsOTPError('');
              }}
              style={{ marginLeft: '10px', padding: '5px 10px', fontSize: '12px' }}
            >
              Clear & Retry
            </button>
          </div>
        )}
        {/* Show resend button if OTP was sent and not verified, OR if there's an error */}
        {(smsOTPSent && !smsOTPVerified) || smsOTPError ? (
          <button
            type="button"
            className="resend-btn"
            onClick={() => {
              console.log('Resend button clicked:', {
                smsOTPSent,
                smsOTPVerified,
                smsOTPError,
                smsOTPLoading
              });
              resendSMSOTPCode();
            }}
            disabled={smsOTPLoading}
          >
            {smsOTPError ? 'Request New OTP' : 'Resend SMS OTP'}
          </button>
        ) : null}
        </div>
      </div>

      <div className="otp-actions">
        {/* <button
          type="button"
          className="back-to-registration-btn"
          onClick={() => setCurrentStep(1)}
        >
          ← Back to Registration
        </button> */}
      </div>
    </div>
    )
  }

  // renderEmailOTPVerificationStep function - REMOVED (no longer needed)

  const renderPaymentStep = () => {
    if (paymentStatus === 'success') {
      return (
        <div className="payment-success center">
          {/* <div className="success-icon">✓</div> */}
          <h2 style={{ color: 'green' }}>Welcome to the HabitCircles family!</h2>
          <p style={{ fontSize: '16px', color: 'black' }}>We are excited to invite you to inspire people to collectively clock a billion healthy habits, towards health, happiness & success!</p>
          <p style={{ fontSize: '16px', color: 'black' }}>Here's your First-time Passcode to sign in!</p>
          <div className="passcode-container">
            <div className="passcode-display">
              {registeredUser?.activation_code || 'Loading...'}
            </div>
          </div>
          {/* Debug info - remove in production */}
          {/* {process.env.NODE_ENV === 'development' && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
              Debug: registeredUser = {JSON.stringify(registeredUser, null, 2)}
            </div>
          )} */}
          <p style={{ fontSize: '16px', color: 'black' }}>This activation code has also been sent to your phone number via SMS. </p>
          <p style={{ 
            fontSize: '16px', 
            color: 'red', 
            fontWeight: "bold",
            animation: 'blink 1s infinite'
          }}>Please check your SMS messages.</p>
          
          {/* Mobile App Download Button */}
          {/* <div className="hc_download_android_contianer" style={{ marginTop: '30px' }}>
            <a 
              className="hc_download_android_btn" 
              href={version?.apk_url || ''} 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={(e) => {
                console.log('Download button clicked:', { version, apk_url: version?.apk_url });
                if (!version?.apk_url) {
                  console.log('Using fallback URL: Google Play Store');
                }
              }}
            >
              <img
                src="../assets/images/HabitCircles/android_robot.svg"
                className="waving_"
                width="20"
                height="20"
                style={{ marginRight: "10px" }}
              />
              DOWNLOAD HABITCIRCLES
            </a>
            <div className="hc_download_android_btn_text">
              Available for Android now, iOS version coming soon!
            </div>
            <div className="hc_download_android_btn_text" style={{ marginTop: '10px' }}>
              For any queries, please contact us at <a href="mailto:hello@heartfultech.com">hello@heartfultech.com</a>
            </div>
          </div> */}
          {/* Debug section - commented out
          {process.env.NODE_ENV === 'development' && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
              Debug: Version loaded: {version ? 'Yes' : 'No'}, URL: {version?.apk_url || 'Not available'}
            </div>
          )}
          {!version && (
            <div style={{ marginTop: '10px' }}>
              <button 
                onClick={() => {
                  console.log('Manual retry for version fetch...');
                  const getVersionDetails = async () => {
                    try {
                      const result = await getVersionDetailsData();
                      if (result.status === 200) {
                        setVersion(result?.data?.data);
                      }
                    } catch (error) {
                      console.error('Manual retry failed:', error);
                    }
                  };
                  getVersionDetails();
                }}
                style={{ 
                  padding: '8px 16px', 
                  fontSize: '12px', 
                  backgroundColor: '#50288c', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Retry Download Link
              </button>
            </div>
          )}
          */}
        </div>
      )
    }

    if (paymentStatus === 'cancelled') {
      return (
        <div className="payment-cancelled">
          <div className="cancel-icon">✕</div>
          <h2>Payment Cancelled</h2>
          <p>You cancelled the payment process.</p>
          <button onClick={handlePayment} className="retry-button">
            Try Again
          </button>
        </div>
      )
    }

    if (paymentStatus === 'failed') {
      return (
        <div className="payment-failed">
          <div className="failed-icon">⚠️</div>
          <h2>Payment Failed</h2>
          <p>There was an issue processing your payment.</p>
          <p>Please try again or contact support if the problem persists.</p>
          <button onClick={handlePayment} className="retry-button">
            Try Again
          </button>
        </div>
      )
    }

    return (
      <div className="payment-step">
        <div className="payment-header">
          {/* <h3>Complete Your Subscription</h3>
          <p>Unlock premium features for your habit tracking journey</p> */}
          {/* <button
            type="button"
            className="back-to-registration-btn"
            onClick={() => {
              setCurrentStep(1)
              setPaymentStatus('pending')
            }}
          >
            ← Back to Registration
          </button> */}
        </div>

        {paymentData && (
          <div className="payment-card">
            <div className="plan-details">
              {/* <h4>{paymentData.name}</h4>
              <p className="plan-description">{paymentData.description}</p> */}
              <div className="features-list">
                <h5>What you'll get:</h5>
                <ul>
                  {FEATURES_LIST?.map((feature, index) => (
                    <li key={index}>
                      <span className="feature-icon">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="price-section" style={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <div>
                <span className="currency">₹</span>
                <span className="amount">{service ? calculateServiceTotal() : (paymentData?.amount || 0)}</span>
                <span className="period">/year</span>
                </div>
              </div>
            </div>

            <div className="payment-methods">
              <h5>Payment Methods</h5>
              <div className="payment-options">
                <div className="payment-option">
                  <span className="option-icon">💳</span>
                  <span>Credit/Debit Cards</span>
                </div>
                <div className="payment-option">
                  <span className="option-icon">📱</span>
                  <span>UPI (Google Pay, PhonePe, Paytm)</span>
                </div>
                <div className="payment-option">
                  <span className="option-icon">🏦</span>
                  <span>Net Banking</span>
                </div>
              </div>
            </div>

            <button
              onClick={handlePayment}
              disabled={isLoading}
              className="pay-button"
            >
              {isLoading ? 'Processing...' : `Pay ₹${service ? calculateServiceTotal() : (paymentData?.amount || 0)}/year`}
            </button>

            <div className="security-info">
              <p>🔒 Your payment is secure and encrypted</p>
              <p>🌍 International payments supported</p>
            </div>
          </div>
        )}
      </div>
    )
  }

  const handleClose = () => {
    if (currentStep === 2) {
      const confirmed = window.confirm(
        'You have successfully registered but haven\'t completed the signup process yet. ' +
        'If you close now, you can resume later from where you left off. ' +
        'Are you sure you want to close?'
      )
      if (confirmed) {
        // Don't clear localStorage - allow user to resume later
        onClose()
      }
    } else if (paymentStatus === 'success') {
      // Clear localStorage after successful payment when user closes
      clearLocalStorage()
      onClose()
    } else {
      // Clear localStorage only if user closes at step 1 (registration)
      clearLocalStorage()
      onClose()
    }
  }

  const content = (
    <div className={`signup-modal ${paymentStatus === 'success' ? 'welcome-modal' : ''} ${renderAsPage ? 'signup-modal-page' : ''}`}>
      {!renderAsPage && (
        <div className="signup-modal-header">
          <button className="back-btn" onClick={handleClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.5 10L12.5 5M7.5 10L12.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h2>
            {currentStep === 1 ? '' : 'Your Subscription'}
          </h2>
        </div>
      )}
        <div className="signup-modal-page-header">
          <h2 className="offering-payment-title" style={{ textAlign: 'center', fontSize: '16px' }}>Please enter your details</h2>
          {/* <p className="offering-payment-subtitle">Complete your purchase by providing your payment details.</p> */}
        </div>
      {currentStep === 1 ? renderRegistrationStep() : 
       currentStep === 2 ? renderSMSOTPVerificationStep() : 
       renderPaymentStep()}
    </div>
  );

  if (renderAsPage) {
    return content;
  }

  return (
    <div className="signup-modal-overlay">
      {content}
    </div>
  )
}

export default SignupModal
